import React, { useState } from 'react';
import { pickResultsService, JobResult } from '../../services/pickResultsService';
import { CURRENT_SEASON, AVAILABLE_SEASONS, getCurrentNFLWeek } from '../../config/season';
import { API_BASE_URL } from '../../lib/config';

interface StandingsEntry {
  rank: number;
  user_id: string;
  username: string;
  display_name: string;
  wins: number;
  losses: number;
  pushes: number;
  win_percentage: string;
}

const PickResultsController: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<JobResult | null>(null);
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentNFLWeek());
  const [standings, setStandings] = useState<StandingsEntry[]>([]);
  const [standingsError, setStandingsError] = useState('');

  // Re-pull the season standings so this panel reflects the numbers a stats
  // calculation just wrote, without having to switch to the Standings tab.
  const refreshStandingsPreview = async (season: number) => {
    try {
      setStandingsError('');
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/standings/season/${season}`);
      if (!response.ok) throw new Error('Failed to fetch standings');
      setStandings(await response.json());
    } catch (error) {
      setStandingsError(error instanceof Error ? error.message : 'Failed to load standings');
    }
  };

  const handleProcessCurrentWeek = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await pickResultsService.processCurrentWeekData(selectedSeason);
      setLastResult(result);
      console.log('Data processing completed:', result);
      if (result.errors.length === 0) await refreshStandingsPreview(selectedSeason);
    } catch (error) {
      console.error('Error processing data:', error);
      setLastResult({
        gamesProcessed: 0,
        picksCalculated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCalculateUserStats = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await pickResultsService.calculateUserStats(selectedSeason);
      setLastResult(result);
      console.log('User stats calculation completed:', result);
      if (result.errors.length === 0) await refreshStandingsPreview(selectedSeason);
    } catch (error) {
      console.error('Error calculating user stats:', error);
      setLastResult({
        gamesProcessed: 0,
        picksCalculated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessSpecificWeek = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await pickResultsService.processWeeklyGameData(selectedSeason, selectedWeek);
      setLastResult(result);
      console.log('Data processing completed:', result);
      if (result.errors.length === 0) await refreshStandingsPreview(selectedSeason);
    } catch (error) {
      console.error('Error processing data:', error);
      setLastResult({
        gamesProcessed: 0,
        picksCalculated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCalculateWeekUserStats = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await pickResultsService.calculateUserStats(selectedSeason, selectedWeek);
      setLastResult(result);
      console.log('User stats calculation completed:', result);
      if (result.errors.length === 0) await refreshStandingsPreview(selectedSeason);
    } catch (error) {
      console.error('Error calculating user stats:', error);
      setLastResult({
        gamesProcessed: 0,
        picksCalculated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <div className="pick-results-controller" style={{ 
      padding: '20px', 
      border: '1px solid #ddd', 
      margin: '20px 0', 
      borderRadius: '8px',
      backgroundColor: '#f8f9fa'
    }}>
      <h2>Game Data Processor & Stats Calculator</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="season-select" style={{ marginRight: '10px' }}>
            Season:
          </label>
          <select
            id="season-select"
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
            disabled={isProcessing}
            style={{ padding: '5px', marginRight: '15px' }}
          >
            {AVAILABLE_SEASONS.map((season) => (
              <option key={season} value={season}>{season}</option>
            ))}
          </select>
          
          <button
            onClick={handleProcessCurrentWeek}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              backgroundColor: isProcessing ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          >
            {isProcessing ? 'Processing...' : 'Process Current Week Data'}
          </button>
          
          <button
            onClick={handleCalculateUserStats}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              backgroundColor: isProcessing ? '#ccc' : '#007cba',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer'
            }}
          >
            {isProcessing ? 'Processing...' : 'Calculate Season Stats'}
          </button>
        </div>

        <div style={{ marginBottom: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
          <label htmlFor="week-select" style={{ marginRight: '10px' }}>
            Specific Week:
          </label>
          <select
            id="week-select"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
            disabled={isProcessing}
            style={{ padding: '5px', marginRight: '15px' }}
          >
            {Array.from({length: 18}, (_, i) => i + 1).map(week => (
              <option key={week} value={week}>Week {week}</option>
            ))}
          </select>
          
          <button
            onClick={handleProcessSpecificWeek}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              backgroundColor: isProcessing ? '#ccc' : '#007cba',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          >
            {isProcessing ? 'Processing...' : `Process Week ${selectedWeek} Data`}
          </button>
          
          <button
            onClick={handleCalculateWeekUserStats}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              backgroundColor: isProcessing ? '#ccc' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer'
            }}
          >
            {isProcessing ? 'Processing...' : `Calculate Week ${selectedWeek} Stats`}
          </button>
        </div>
      </div>

      {lastResult && (
        <div className="results-summary" style={{ 
          marginTop: '20px',
          padding: '15px',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}>
          <h3>Last Job Results</h3>
          <div style={{ marginBottom: '10px' }}>
            <strong>Timestamp:</strong> {formatTimestamp(lastResult.timestamp)}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Games Processed:</strong> {lastResult.gamesProcessed}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Picks Calculated:</strong> {lastResult.picksCalculated}
          </div>

          {lastResult.errors.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <h4 style={{ color: 'red' }}>Errors:</h4>
              <ul style={{ color: 'red' }}>
                {lastResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {lastResult.errors.length === 0 && lastResult.gamesProcessed > 0 && (
            <div style={{ color: 'green', marginTop: '10px', fontWeight: 'bold' }}>
              ✅ Job completed successfully!
            </div>
          )}

          {lastResult.gamesProcessed === 0 && lastResult.errors.length === 0 && (
            <div style={{ color: '#856404', marginTop: '10px' }}>
              ⚠️ No games needed processing (all up to date)
            </div>
          )}
        </div>
      )}

      {standingsError && (
        <div style={{ marginTop: '15px', color: 'red' }}>
          Could not load standings preview: {standingsError}
        </div>
      )}

      {standings.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}>
          <h3>Standings — {selectedSeason} Season (updated)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '6px' }}>#</th>
                <th style={{ padding: '6px' }}>Player</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>W</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>L</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>P</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>Win %</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((entry) => (
                <tr key={entry.user_id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px' }}>{entry.rank}</td>
                  <td style={{ padding: '6px' }}>{entry.display_name} <span style={{ color: '#888' }}>@{entry.username}</span></td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{entry.wins}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{entry.losses}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{entry.pushes}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>{entry.win_percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
        <p><strong>Data Processing:</strong></p>
        <ul>
          <li>Fetches latest scores from ESPN API for Sunday games</li>
          <li>Updates game scores and final status in database</li>
          <li>Only processes games that have new scores/status changes</li>
        </ul>
        
        <p><strong>Stats Calculation:</strong></p>
        <ul>
          <li>Calculates pick results using spread logic (win/lose/push)</li>
          <li>Updates user_stats table with picks_right, picks_wrong, pushes</li>
          <li>Handles exact spread scenarios (pushes)</li>
        </ul>
        
        <p><strong>Typical Schedule:</strong></p>
        <ul>
          <li>Sunday 4:00 PM - After early games finish</li>
          <li>Sunday 7:30 PM - After late afternoon games</li>
          <li>Sunday 11:00 PM - After night games</li>
        </ul>
      </div>
    </div>
  );
};

export default PickResultsController;